import { Grid, TextField, FormHelperText } from '@material-ui/core/';
import React, { useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldError, useFormContext } from 'react-hook-form';
import { getValidator, numberWithSpaces, UserRole } from '../../Constants/constants';
import Helper from '../../Utils/Helpers';
import { DictUserRole } from '../../API/Interfaces/IUserDtos';
import SelectLocation, { ISelectLocationProps } from '../../Layout/Components/SelectLocation';
import { isInRole } from '../../Utils/roleChecker';
import { UserContext } from '../../Context/UserContext';

interface IGoalFormProps {
	disabled?: boolean;
}

export default function GoalForm(props: IGoalFormProps) {
	const { t } = useTranslation();
	const { errors, register, setValue } = useFormContext();
	const userContext = useContext(UserContext);

	const formLabels = {
		name: t('repository.goals.goalName'),
		title: t('repository.goals.goalTitle'),
		targetAmount: t('repository.goals.targetAmount'),
		collectedAmount: t('repository.goals.collectedAmount'),
		description: t('repository.goals.description'),
		corporation: t('common.form.selectedCorporation'),
	};

	const formValidators = {
		name: getValidator(t, 40, null, true),
		title: getValidator(t, 100, null, true),
		targetAmount: getValidator(t, null, null, false),
		collectedAmount: getValidator(t, null, null, false),
		description: getValidator(t, 315, null, true),
	};

	const handleTargetAmountOnChange = (e) => {
		Helper.handleOnlyDigits(e);
		setValue(
			e.target.name,
			e.target.value > 999999999 ? '999999999' : numberWithSpaces(e.target.value.replace(' ', '').replace(',', '.')).toString()
		);
	};

	const selectLocationProps: ISelectLocationProps = {
		userFormType: DictUserRole.CorporationAdmin,
	};

	useEffect(() => {
		if (!(isInRole(userContext.userRoles, UserRole.DealerAdmin) || isInRole(userContext.userRoles, UserRole.SuperAdmin))) {
			setValue('corporationId', userContext.userWorkplaceId);
		}
	});

	return (
		<>
			<input name='id' ref={register()} hidden={true} />
			<input name='pictureFile' type='file' ref={register} hidden />

			<Grid item xs={12}>
				<TextField
					error={errors.name !== undefined}
					helperText={(errors.name as FieldError)?.message}
					label={formLabels.name}
					name='name'
					inputRef={register(formValidators.name)}
					fullWidth
					disabled={props.disabled}
				/>
			</Grid>
			<Grid item xs={12}>
				<TextField
					error={errors.title !== undefined}
					helperText={(errors.title as FieldError)?.message}
					label={formLabels.title}
					name='title'
					inputRef={register(formValidators.title)}
					fullWidth
					disabled={props.disabled}
				/>
			</Grid>
			<Grid item xs={6}>
				<TextField
					error={errors.targetAmount !== undefined}
					helperText={(errors.targetAmount as FieldError)?.message}
					label={formLabels.targetAmount}
					name='targetAmount'
					type='text'
					inputRef={register(formValidators.targetAmount)}
					fullWidth
					onChange={handleTargetAmountOnChange}
					disabled={props.disabled}
				/>
			</Grid>
			<Grid item xs={6}>
				<TextField
					error={errors.currentAmount !== undefined}
					helperText={(errors.currentAmount as FieldError)?.message}
					label={formLabels.collectedAmount}
					name='currentAmount'
					type='text'
					inputRef={register(formValidators.collectedAmount)}
					fullWidth
					onChange={handleTargetAmountOnChange}
					disabled={props.disabled}
				/>
			</Grid>

			<Grid item xs={12}>
				<TextField
					error={errors.description !== undefined}
					helperText={(errors.description as FieldError)?.message}
					label={formLabels.description}
					name='description'
					inputRef={register(formValidators.description)}
					fullWidth
					multiline
					disabled={props.disabled}
				/>
			</Grid>

			{isInRole(userContext.userRoles, UserRole.DealerAdmin) || isInRole(userContext.userRoles, UserRole.SuperAdmin) ? (
				<>
					<SelectLocation {...selectLocationProps} />
					<Grid item xs={6} />
				</>
			) : (
				<input hidden name='corporationId' ref={register()}></input>
			)}

			<div style={{ position: 'absolute', bottom: 0, left: 1 }}>
				{errors.image !== undefined && (
					<FormHelperText error={errors.image !== undefined}>{(errors.image as FieldError)?.message}</FormHelperText>
				)}
			</div>
		</>
	);
}
